import type { BootstrapPayload, Command, IOmniSearchService } from "../../core";
import type { CommandPaletteSource } from "../types";
import { buildQuickActionsSource } from "./quick-actions";
import { buildNavigateSource } from "./navigate";
import { buildOmniSearchSource } from "./omni-search";
import { buildChatSource } from "./chat";
import { buildActionsSource } from "./actions";
import { buildAppsSource } from "./apps";
import { buildMenusSource } from "./menus";

type Dispatch = (cmd: Command) => Promise<unknown>;

/**
 * Default client-side source set. Returned in registration order so the
 * engine groups predictably:
 *
 *   1. Quick actions — empty-query landing only (silent once you type).
 *   2. Navigate to   — apps + menus + actions consolidated into one
 *      breadcrumb-formatted group ("/" mode). Matches the canonical UX.
 *   3. Records       — cross-model record omni-search (default free-text
 *      mode); backed by GET /api/web/omni-search (PC-10).
 *   4. Chat and AI   — "@" prefix only.
 *
 * apps/menus/actions sources still exist on disk (unused) — left as a safety
 * net while the consolidated navigate source bakes.
 */
export function buildClientSources(
    boot: BootstrapPayload,
    dispatch: Dispatch,
    omniSearch: IOmniSearchService,
    getCurrentAppId?: () => string | undefined,
): CommandPaletteSource[] {
    // Reference the legacy builders so they stay type-checked even while
    // unused — easy to flip back if we ever want differentiated groups.
    void buildAppsSource;
    void buildMenusSource;
    void buildActionsSource;

    return [
        buildQuickActionsSource(boot, dispatch),
        buildNavigateSource(boot, dispatch, getCurrentAppId),
        buildOmniSearchSource(omniSearch, dispatch),
        buildChatSource(boot, dispatch),
    ];
}

export {
    buildQuickActionsSource,
    buildNavigateSource,
    buildOmniSearchSource,
    buildChatSource,
    buildAppsSource,
    buildMenusSource,
    buildActionsSource,
};
